/**
 * commentAutocompletePlugin.js
 *
 * @mention autocomplete for the CommentEditor.
 * Stripped-down version of the main autocompletePlugin — only handles @-triggers
 * (no hashtags).  Uses the same searchUsersForMention helper and the same
 * insertMention logic (creates a userMention inline node from commentSchema).
 *
 * Returns an array of plugins from prosemirror-autocomplete, same as the
 * main createAutocompletePlugins() function.
 */

import autocomplete from 'prosemirror-autocomplete';
import {searchUsersForMention} from '../../prosemirror-editor/helpers/mentions/searchUsers';

export function createCommentAutocompletePlugin() {
    // State maintained in closure
    let dropdown = null;
    let items = [];
    let selectedIndex = 0;
    let currentView = null;
    let currentRange = null;
    let clickOutsideHandler = null;

    function cleanupDropdown() {
        if (dropdown && dropdown.parentNode) {
            dropdown.parentNode.removeChild(dropdown);
        }
        if (clickOutsideHandler) {
            document.removeEventListener('mousedown', clickOutsideHandler);
            clickOutsideHandler = null;
        }
        dropdown = null;
        items = [];
        selectedIndex = 0;
        currentView = null;
        currentRange = null;
    }

    function selectItem(index) {
        if (!currentView || !currentRange) return false;
        if (items.length === 0 || index >= items.length) return false;
        insertMention(currentView, currentRange, items[index]);
        cleanupDropdown();
        return true;
    }

    function createDropdown(coords) {
        const container = document.createElement('div');
        container.className = 'prosemirror-suggestion comment-mention-suggestion';
        container.setAttribute('role', 'listbox');
        container.setAttribute('aria-label', 'Mention suggestions');
        container.style.cssText = `
            position: fixed;
            top: ${coords.bottom}px;
            left: ${coords.left}px;
            z-index: 9999;
            background: white;
            border: 1px solid #ccc;
            border-radius: 4px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            max-height: 240px;
            overflow-y: auto;
            min-width: 200px;
        `;
        document.body.appendChild(container);

        const handleClickOutside = (e) => {
            if (container && !container.contains(e.target)) {
                cleanupDropdown();
                document.removeEventListener('mousedown', handleClickOutside);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        clickOutsideHandler = handleClickOutside;
        return container;
    }

    function positionDropdown(coords) {
        if (!dropdown) return;
        dropdown.style.top = `${coords.bottom}px`;
        dropdown.style.left = `${coords.left}px`;
    }

    function renderDropdown(container, itemList, selIdx) {
        if (!container) return;
        container.innerHTML = '';

        if (itemList.length === 0) {
            const empty = document.createElement('div');
            empty.style.cssText = 'padding: 10px 12px; color: #999; font-size: 0.875rem;';
            empty.textContent = 'No users found';
            container.appendChild(empty);
            return;
        }

        const ul = document.createElement('ul');
        ul.style.cssText = 'list-style: none; margin: 0; padding: 0;';
        ul.setAttribute('role', 'presentation');

        itemList.forEach((item, i) => {
            const li = document.createElement('li');
            li.setAttribute('role', 'option');
            li.setAttribute('aria-selected', i === selIdx ? 'true' : 'false');
            li.style.cssText = `
                padding: 7px 12px;
                cursor: pointer;
                display: flex;
                align-items: center;
                gap: 8px;
                background-color: ${i === selIdx ? '#f0f0f0' : 'white'};
                font-size: 0.875rem;
            `;

            if (item.headshot) {
                const img = document.createElement('img');
                img.src = item.headshot;
                img.alt = item.label || '';
                img.width = 28;
                img.height = 28;
                img.style.cssText = 'border-radius: 50%; flex-shrink: 0;';
                li.appendChild(img);
            }

            const text = document.createElement('div');
            const name = document.createElement('div');
            name.style.cssText = 'font-weight: 500; color: #333;';
            name.textContent = item.label || item.name || '';
            text.appendChild(name);

            if (item.position || item.email) {
                const sub = document.createElement('div');
                sub.style.cssText = 'font-size: 0.75rem; color: #666;';
                sub.textContent = item.position || item.email;
                text.appendChild(sub);
            }
            li.appendChild(text);

            li.onmouseenter = () => {
                selectedIndex = i;
                Array.from(ul.children).forEach((child, idx) => {
                    child.style.backgroundColor = idx === i ? '#f0f0f0' : 'white';
                    child.setAttribute('aria-selected', idx === i ? 'true' : 'false');
                });
            };

            li.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                selectItem(i);
            };

            ul.appendChild(li);
        });

        container.appendChild(ul);
    }

    function insertMention(view, range, item) {
        const {state} = view;
        const schema = state.schema;
        if (!schema.nodes.userMention) {
            console.error('[commentAutocomplete] userMention node not in commentSchema');
            return;
        }
        const mentionNode = schema.nodes.userMention.create({
            id: item.id,
            label: item.label || item.name,
            name: item.name,
            email: item.email,
            position: item.position,
            headshot: item.headshot,
        });
        const {from, to} = range;
        const tr = state.tr
            .deleteRange(from, to)
            .insert(from, mentionNode)
            .insertText('\u00A0'); // non-breaking space after mention
        view.dispatch(tr);
    }

    const triggers = [
        {
            name: 'mention',
            trigger: '@',
            cancelOnFirstSpace: false,
        },
    ];

    return autocomplete({
        triggers,

        onOpen: async ({view, range, filter}) => {
            currentView = view;
            currentRange = range;
            const coords = view.coordsAtPos(range.from);
            dropdown = createDropdown(coords);
            try {
                items = await searchUsersForMention(filter);
                selectedIndex = 0;
                renderDropdown(dropdown, items, selectedIndex);
            } catch (err) {
                console.error('[commentAutocomplete] search error:', err);
                items = [];
                renderDropdown(dropdown, items, selectedIndex);
            }
            return true;
        },

        onFilter: async ({view, range, filter}) => {
            currentView = view;
            currentRange = range;
            try {
                items = await searchUsersForMention(filter);
                selectedIndex = 0;
                if (dropdown) renderDropdown(dropdown, items, selectedIndex);
            } catch (err) {
                items = [];
                if (dropdown) renderDropdown(dropdown, items, selectedIndex);
            }
            const coords = view.coordsAtPos(range.from);
            positionDropdown(coords);
            return true;
        },

        onArrow: ({kind}) => {
            if (!items.length) return false;
            if (kind === 'ArrowDown') {
                selectedIndex = (selectedIndex + 1) % items.length;
            } else if (kind === 'ArrowUp') {
                selectedIndex = (selectedIndex - 1 + items.length) % items.length;
            } else {
                return false;
            }
            if (dropdown) {
                renderDropdown(dropdown, items, selectedIndex);
                dropdown.querySelector('[aria-selected="true"]')?.scrollIntoView({block: 'nearest'});
            }
            return true;
        },

        onEnter: ({view, range}) => {
            if (!dropdown || items.length === 0) {
                cleanupDropdown();
                return false;
            }
            currentView = view;
            currentRange = range;
            insertMention(view, range, items[selectedIndex]);
            cleanupDropdown();
            return true;
        },

        onClose: () => {
            cleanupDropdown();
            return true;
        },
    });
}

